import axiosInstance from '../utils/axiosInstance';
import config from '../config.json';
import url from '../url.json';

// ==================== 活動相關 API ====================

/**
 * 取得學生在班級中的活動
 * @param {string|number} classId - 班級ID
 * @returns {Promise<Object>} 活動列表
 */
export const getClassActivitiesForStudent = async (classId) => {
    try {
        const response = await axiosInstance.get(
            `${url.backendHost}${config.getClassActivitiesForStudent}${classId}/student`
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching class activities for student:', error);
        throw error;
    }
};

/**
 * 建立新活動
 * @param {Object} activityData - 活動資料
 * @returns {Promise<Object>} 建立的活動
 */
export const createActivity = async (activityData) => {
    try {
        const response = await axiosInstance.post(
            `${url.backendHost}${config.createActivity}`,
            activityData
        );
        return response.data;
    } catch (error) {
        console.error('Error creating activity:', error);
        throw error;
    }
};

/**
 * 更新活動
 * @param {string|number} activityId - 活動ID
 * @param {Object} activityData - 更新的活動資料
 * @returns {Promise<Object>} 更新結果
 */
export const updateActivity = async (activityId, activityData) => {
    try {
        const response = await axiosInstance.put(
            `${url.backendHost}${config.updateActivity}${activityId}`,
            activityData
        );
        return response.data;
    } catch (error) {
        console.error('Error updating activity:', error);
        throw error;
    }
};

/**
 * 刪除活動
 * @param {string|number} activityId - 活動ID
 * @returns {Promise<Object>} 刪除結果
 */
export const deleteActivity = async (activityId) => {
    try {
        const response = await axiosInstance.delete(
            `${url.backendHost}${config.deleteActivity}${activityId}`
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting activity:', error);
        throw error;
    }
};

/**
 * 取得活動詳情
 * @param {string|number} activityId - 活動ID
 * @returns {Promise<Object>} 活動詳情
 */
export const getActivity = async (activityId) => {
    try {
        const response = await axiosInstance.get(
            `${url.backendHost}${config.getActivity}${activityId}`
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching activity:', error);
        throw error;
    }
};

// ==================== 學生管理相關 API ====================

/**
 * 取得班級學生列表
 * @param {string|number} classId - 班級ID
 * @returns {Promise<Object>} 學生列表
 */
export const getClassStudents = async (classId) => {
    try {
        const response = await axiosInstance.get(
            `${url.backendHost}${config.getClassStudents}${classId}/students`
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching class students:', error);
        throw error;
    }
};

/**
 * 擴展加入班級功能（支持新用戶註冊）
 * @param {Object} data - 加入班級資料
 * @returns {Promise<Object>} 加入結果
 */
export const joinClassExtended = async (data) => {
    try {
        const response = await axiosInstance.post(
            `${url.backendHost}${config.joinClassExtended}`,
            data
        );
        return response.data;
    } catch (error) {
        console.error('Error joining class extended:', error);
        throw error;
    }
};

/**
 * 批次上傳學生資料
 * @param {Object} data - 批次上傳資料
 * @returns {Promise<Object>} 上傳結果
 */
export const batchUploadStudents = async (data) => {
    try {
        const response = await axiosInstance.post(
            `${url.backendHost}${config.batchUploadStudents}`,
            data
        );
        return response.data;
    } catch (error) {
        console.error('Error batch uploading students:', error);
        throw error;
    }
};

/**
 * 更新用戶資料
 * @param {string|number} userId - 用戶ID
 * @param {Object} userData - 用戶資料
 * @returns {Promise<Object>} 更新結果
 */
export const updateUser = async (userId, userData) => {
    try {
        const response = await axiosInstance.put(
            `${url.backendHost}${config.updateUser}${userId}`,
            userData
        );
        return response.data;
    } catch (error) {
        console.error('Error updating user:', error);
        throw error;
    }
};

/**
 * 刪除用戶
 * @param {string|number} userId - 用戶ID
 * @returns {Promise<Object>} 刪除結果
 */
export const deleteUser = async (userId) => {
    try {
        const response = await axiosInstance.delete(
            `${url.backendHost}${config.deleteUser}${userId}`
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
    }
};

// ==================== 分組管理相關 API ====================

/**
 * 取得班級分組列表
 * @param {string|number} classId - 班級ID
 * @returns {Promise<Object>} 分組列表
 */
export const getClassGroups = async (classId) => {
    try {
        const response = await axiosInstance.get(
            `${url.backendHost}${config.getClassGroups}${classId}/groups`
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching class groups:', error);
        throw error;
    }
};

/**
 * 建立分組
 * @param {Object} data - 分組資料 { groupName, classId, activityId? }
 * @returns {Promise<Object>} 建立結果
 */
export const createGroup = async (data) => {
    try {
        const response = await axiosInstance.post(
            `${url.backendHost}${config.createGroup}`,
            data
        );
        return response.data;
    } catch (error) {
        console.error('Error creating group:', error);
        throw error;
    }
};

/**
 * 分配學生到分組
 * @param {string|number} studentId - 學生ID
 * @param {string|number} groupId - 分組ID
 * @returns {Promise<Object>} 分配結果
 */
export const assignStudentToGroup = async (studentId, groupId) => {
    try {
        const response = await axiosInstance.post(
            `${url.backendHost}${config.assignStudentToGroup}`,
            { studentId, groupId }
        );
        return response.data;
    } catch (error) {
        console.error('Error assigning student to group:', error);
        throw error;
    }
};

/**
 * 從分組移除學生
 * @param {string|number} studentId - 學生ID
 * @param {string|number} groupId - 分組ID
 * @returns {Promise<Object>} 移除結果
 */
export const removeStudentFromGroup = async (studentId, groupId) => {
    try {
        const response = await axiosInstance.post(
            `${url.backendHost}${config.removeStudentFromGroup}`,
            { studentId, groupId }
        );
        return response.data;
    } catch (error) {
        console.error('Error removing student from group:', error);
        throw error;
    }
};

/**
 * 刪除分組
 * @param {string|number} groupId - 分組ID
 * @returns {Promise<Object>} 刪除結果
 */
export const deleteGroup = async (groupId) => {
    try {
        const response = await axiosInstance.delete(
            `${url.backendHost}${config.deleteGroup}${groupId}`
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting group:', error);
        throw error;
    }
};

/**
 * 主持人分配學生到活動小組
 * @param {string|number} activityId - 活動ID
 * @param {Array} assignments - 分配資料陣列
 * @returns {Promise<Object>} 分配結果
 */
export const assignStudentsToGroups = async (activityId, assignments) => {
    try {
        const response = await axiosInstance.post(
            `${url.backendHost}${config.assignStudentsToGroups}`,
            { activityId, assignments }
        );
        return response.data;
    } catch (error) {
        console.error('Error assigning students to groups:', error);
        throw error;
    }
};

// ==================== 分組管理工具函數 ====================

/**
 * 建立預設分組
 * @param {string|number} classId - 班級ID
 * @param {Array<string>} groupNames - 分組名稱陣列
 * @returns {Promise<Array>} 建立的分組陣列
 */
export const createDefaultGroups = async (classId, groupNames = ['第1組', '第2組', '第3組', '第4組', '第5組']) => {
    try {
        const promises = groupNames.map(groupName => 
            createGroup({ classId: parseInt(classId), groupName })
        );
        const results = await Promise.all(promises);
        return results;
    } catch (error) {
        console.error('Error creating default groups:', error);
        throw error;
    }
};

/**
 * 套用預設分組到活動
 * @param {string|number} classId - 班級ID
 * @param {string|number} activityId - 活動ID
 * @returns {Promise<Array>} 建立的活動分組陣列
 */
export const applyDefaultGroupsToActivity = async (classId, activityId) => {
    try {
        // 先取得預設分組
        const defaultGroupsResponse = await getClassGroups(classId);
        const defaultGroups = defaultGroupsResponse.data?.filter(g => !g.activityId) || [];
        
        if (defaultGroups.length === 0) {
            throw new Error('沒有預設分組可以套用');
        }
        const numericClassId = parseInt(classId);
        const numericActivityId = parseInt(activityId);

        // 刪除現有的活動分組以避免重名衝突
        const existingActivityGroups = (defaultGroupsResponse.data || []).filter(g => g.activityId === numericActivityId);
        for (const g of existingActivityGroups) {
            try {
                await deleteGroup(g.id);
            } catch (e) {
                // 忽略個別刪除失敗，繼續進行
                // console.warn('Delete existing activity group failed:', g.id, e?.response?.data || e?.message);
            }
        }

        // 為每個預設分組建立對應的活動分組
        const createResults = await Promise.all(
            defaultGroups.map(defaultGroup => 
                createGroup({
                    classId: numericClassId,
                    groupName: defaultGroup.groupName,
                    activityId: numericActivityId
                })
            )
        );

        // 建立 groupName -> 新 groupId 的對應
        const nameToNewGroupId = new Map();
        createResults.forEach(res => {
            const group = res?.data || res?.data?.data || res?.data; // 兼容不同層級
            const payload = res?.data?.data ? res.data.data : (res?.data?.id ? res.data : res?.data || res);
            const groupData = res?.data?.data || res?.data || res;
            const gName = groupData?.groupName || groupData?.name;
            const gId = groupData?.id;
            if (gName && gId) nameToNewGroupId.set(gName, gId);
        });

        // 構建學生 assignments：將預設分組中的學生對應到新活動分組
        const assignments = [];
        defaultGroups.forEach(dg => {
            const gName = dg.groupName || dg.name;
            const targetGroupId = nameToNewGroupId.get(gName);
            if (!targetGroupId) return;
            const students = Array.isArray(dg.students) ? dg.students : [];
            students.forEach(stu => {
                if (stu?.id) assignments.push({ studentId: stu.id, groupId: targetGroupId });
            });
        });

        if (assignments.length > 0) {
            await assignStudentsToGroups(numericActivityId, assignments);
        }

        return { success: true, created: createResults.length, assigned: assignments.length };
    } catch (error) {
        console.error('Error applying default groups to activity:', error);
        throw error;
    }
};

/**
 * 取得分組中的學生資料
 * @param {Array} groups - 分組陣列
 * @param {Array} students - 學生陣列
 * @returns {Array} 包含學生資料的分組陣列
 */
export const getGroupsWithStudents = (groups, students) => {
    return groups.map(group => ({
        ...group,
        students: students.filter(student => 
            group.userId && group.userId.includes(student.id)
        )
    }));
};

/**
 * 取得未分配的學生
 * @param {Array} students - 學生陣列
 * @param {Array} groups - 分組陣列
 * @returns {Array} 未分配的學生陣列
 */
export const getUnassignedStudents = (students, groups) => {
    const assignedStudentIds = new Set();
    groups.forEach(group => {
        if (group.userId) {
            group.userId.forEach(id => assignedStudentIds.add(id));
        }
    });
    
    return students.filter(student => !assignedStudentIds.has(student.id));
}; 